import { z } from "zod";
import { COMP_CATEGORIES, DECIMAL_STRING_REGEX, EVENT_ID_REGEX, PRICE_CATEGORIES, } from "./constants.js";
/** Decimal CHF string validator — `^\d+\.\d{2}$`. */
export const decimalStringSchema = z
    .string()
    .regex(DECIMAL_STRING_REGEX, "Must be a decimal CHF string like '15.00'");
export const priceCategorySchema = z.enum(PRICE_CATEGORIES);
export const compCategorySchema = z.enum(COMP_CATEGORIES);
export const eventIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(EVENT_ID_REGEX, "eventId must be a lowercase slug");
export const calendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD")
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), {
    message: "Not a valid calendar date",
});
export const isoUtcSchema = z
    .string()
    .refine((s) => !Number.isNaN(Date.parse(s)) && /Z$|[+-]\d{2}:?\d{2}$/.test(s), { message: "Must be an ISO 8601 timestamp" });
export const pricesSchema = z.object({
    adult_member: decimalStringSchema,
    adult_nonmember: decimalStringSchema,
    child_member: decimalStringSchema,
    child_nonmember: decimalStringSchema,
});
export const purchaseItemSchema = z.object({
    category: priceCategorySchema,
    quantity: z.number().int().min(1).max(50),
});
/**
 * SPEC §4.1 — accompanying-adult invariants. `adult_member` is the "free
 * accompanying adult" slot (price "0.00" on the event doc). The buy page
 * surfaces it as "Begleitperson"; the invariants below make sure that
 * semantic holds end-to-end and `amount: "0.00"` can never reach SumUp:
 *
 *   - total `adult_member` quantity ≤ 2 per transaction (anyone needing
 *     more buys at the venue — Abendkasse).
 *   - a cart containing any `adult_member` line must also contain at
 *     least one non-accompanying line (a paying companion item).
 */
export const MAX_ACCOMPANYING_ADULTS_PER_TRANSACTION = 2;
export const createPurchaseSchema = z
    .object({
    eventId: eventIdSchema,
    customerEmail: z.string().email().transform((v) => v.trim().toLowerCase()),
    items: z.array(purchaseItemSchema).min(1).max(20),
    isTest: z.boolean(),
})
    .superRefine((val, ctx) => {
    const accompanyingQty = val.items
        .filter((it) => it.category === "adult_member")
        .reduce((n, it) => n + it.quantity, 0);
    const hasPayingCompanion = val.items.some((it) => it.category !== "adult_member");
    if (accompanyingQty > MAX_ACCOMPANYING_ADULTS_PER_TRANSACTION) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["items"],
            message: "accompanying_adult_quantity_exceeded",
        });
    }
    if (accompanyingQty > 0 && !hasPayingCompanion) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["items"],
            message: "accompanying_adult_requires_paying_item",
        });
    }
});
export const createCompSchema = z
    .object({
    eventId: eventIdSchema,
    category: priceCategorySchema,
    issuedFor: z
        .string()
        .email()
        .transform((v) => v.trim().toLowerCase())
        .optional(),
    priceWorth: decimalStringSchema,
    pricePaid: decimalStringSchema,
    compCategory: compCategorySchema,
    compPurpose: z
        .string()
        .max(200)
        .transform((v) => v.trim())
        .refine((v) => v.length > 0, { message: "compPurpose must not be empty" })
        .optional(),
    isTest: z.boolean(),
    sendEmail: z.boolean(),
})
    .superRefine((val, ctx) => {
    // pricePaid <= priceWorth
    const worth = Number(val.priceWorth);
    const paid = Number(val.pricePaid);
    if (paid > worth) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pricePaid"],
            message: "paid_exceeds_worth",
        });
    }
    if (val.sendEmail && !val.issuedFor) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sendEmail"],
            message: "comp_email_requires_recipient",
        });
    }
});
export const createEventSchema = z.object({
    eventId: eventIdSchema,
    name: z.string().min(1).max(120),
    eventDate: calendarDateSchema,
    venue: z.string().min(1).max(200),
    prices: pricesSchema,
    active: z.boolean().optional(),
});
export const updateEventSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    eventDate: calendarDateSchema.optional(),
    venue: z.string().min(1).max(200).optional(),
    prices: pricesSchema.optional(),
    active: z.boolean().optional(),
    salesEndAt: isoUtcSchema.nullable().optional(),
    salesEndMode: z.enum(["paused", "ended"]).nullable().optional(),
    confirmPriceChangeAfterSales: z.boolean().optional(),
})
    .superRefine((val, ctx) => {
    // The pair travels together: a stop needs its instant, re-opening clears both.
    if (val.salesEndMode !== undefined || val.salesEndAt !== undefined) {
        const mode = val.salesEndMode ?? null;
        const at = val.salesEndAt ?? null;
        if ((mode === null) !== (at === null)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["salesEndMode"],
                message: "sales_end_fields_must_pair",
            });
        }
    }
});
export const supportSearchQuerySchema = z
    .object({
    customerEmail: z
        .string()
        .email()
        .transform((v) => v.trim().toLowerCase())
        .optional(),
    transactionId: z.string().min(3).max(80).optional(),
    // Accepts a bare suffix OR a full pasted ticket TypeID ("ticket_" + 26
    // chars = 33) — the prefix is stripped so endsWith-matching still works.
    // The 3-char minimum applies to what REMAINS after stripping: a bare
    // "ticket_" would otherwise become the empty suffix and match everything.
    ticketSuffix: z
        .string()
        .max(33)
        .transform((v) => (v.startsWith("ticket_") ? v.slice("ticket_".length) : v))
        .refine((v) => v.length >= 3, { message: "ticketSuffix too short" })
        .optional(),
    eventId: eventIdSchema.optional(),
})
    .refine((q) => Boolean(q.customerEmail || q.transactionId || q.ticketSuffix), { message: "Provide at least one of customerEmail, transactionId, or ticketSuffix" });
/** SPEC §6.2 `GET /api/admin/comps` — comp-history list. */
export const compListQuerySchema = z.object({
    eventId: eventIdSchema.optional(),
});
export const resendSchema = z.object({
    overrideEmail: z
        .string()
        .email()
        .transform((v) => v.trim().toLowerCase())
        .optional(),
});
export const ackAnomalySchema = z.object({
    note: z
        .string()
        .max(500)
        .transform((v) => v.trim())
        .refine((v) => v.length > 0, { message: "note must not be empty if present" })
        .optional(),
});
export const adminEventListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).default(10),
});
export const adminTransactionListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: z.enum(["pending", "completed", "failed", "abandoned"]).optional(),
    includeTest: z
        .union([z.boolean(), z.string()])
        .transform((v) => (typeof v === "string" ? v === "true" : v))
        .default(true),
});
export const reportsQuerySchema = z.object({
    eventId: eventIdSchema,
    includeTest: z
        .union([z.boolean(), z.string()])
        .transform((v) => (typeof v === "string" ? v === "true" : v))
        .default(false),
});
/**
 * SumUp webhook integrity.
 *
 * SumUp does NOT sign webhook callbacks (confirmed by probing the live API),
 * so we sign the `return_url` we register per-checkout: the URL carries
 * `?txn=<transactionId>&sig=<hmac-hex>`. The receiver validates both.
 */
export const sumupWebhookQuerySchema = z.object({
    txn: z
        .string()
        .min(1)
        .max(80)
        .regex(/^tx_[0-9a-z]+$/i, "txn must be a transaction TypeID"),
    sig: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[0-9a-f]+$/i, "sig must be hex-encoded"),
});
/**
 * SumUp webhook body — observed shape on 2026-04-21 is exactly
 * `{ id, status, event_type }`. No nested payload, no checkout_reference,
 * no amount/currency.
 *   - `id` is SumUp's checkout UUID (matches our stored `sumupCheckoutId`).
 *   - `status` is e.g. "SUCCESSFUL" / "FAILED".
 *   - `event_type` is currently "CHECKOUT_STATUS_CHANGED".
 */
export const sumupWebhookBodySchema = z.object({
    id: z.string().min(1).max(128),
    status: z.string().min(1).max(64),
    event_type: z.string().min(1).max(64),
});
export const anomalyListQuerySchema = z.object({
    acknowledged: z
        .union([z.boolean(), z.string()])
        .transform((v) => (typeof v === "string" ? v === "true" : v))
        .optional(),
    all: z
        .union([z.boolean(), z.string()])
        .transform((v) => (typeof v === "string" ? v === "true" : v))
        .optional(),
});
//# sourceMappingURL=schemas.js.map